/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback } from 'react';

// ── Storage key ───────────────────────────────────────────────────────────────
// Shape: { role: 'senior' | 'support' | 'account_manager', name: string, active: boolean }
export const WM_STAFF_KEY = 'wm-staff-config';

// ── Capability map ────────────────────────────────────────────────────────────
const WM_STAFF_CAPABILITIES = {
  'internal.intelligence':  ['senior', 'support'],
  'internal.shop_profiles': ['senior', 'support'],
  'internal.bugs':          ['senior', 'support'],
  'internal.audit':         ['senior', 'support'],
  'internal.flags':         ['senior'],
  'internal.staff_manage':  ['senior'],
  'org.enter':              ['senior', 'support', 'account_manager'],
  'org.write':              ['senior'],
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function readConfig() {
  try {
    const raw = localStorage.getItem(WM_STAFF_KEY);
    if (!raw) return null;
    const cfg = JSON.parse(raw);
    if (!cfg?.active) return null;
    return cfg;
  } catch {
    return null;
  }
}

export function writeConfig(cfg) {
  try {
    if (cfg === null) {
      localStorage.removeItem(WM_STAFF_KEY);
    } else {
      localStorage.setItem(WM_STAFF_KEY, JSON.stringify(cfg));
    }
  } catch { /* ignore */ }
}

const WmStaffContext = createContext(null);

export function WmStaffProvider({ children }) {
  // Synchronous localStorage read — no async/loading state needed
  const [wmStaffMember, setWmStaffMember] = useState(readConfig);
  const [viewingOrgId, setViewingOrgId]   = useState(null);
  const [viewingOrgName, setViewingOrgName] = useState(null);

  // ── Derived identity ────────────────────────────────────────────────────────
  const isWmStaff = Boolean(wmStaffMember);
  const wmRole    = wmStaffMember?.role ?? null;

  // ── Capability helper ───────────────────────────────────────────────────────
  const wmCan = useCallback((capability) => {
    if (!isWmStaff || !wmRole) return false;
    const allowed = WM_STAFF_CAPABILITIES[capability];
    if (!allowed) return false;
    return allowed.includes(wmRole);
  }, [isWmStaff, wmRole]);

  // ── Config management (called by StaffSettingsTab) ──────────────────────────
  const activateStaff = useCallback((cfg) => {
    writeConfig({ ...cfg, active: true });
    setWmStaffMember({ ...cfg, active: true });
  }, []);

  const deactivateStaff = useCallback(() => {
    writeConfig(null);
    setWmStaffMember(null);
    setViewingOrgId(null);
    setViewingOrgName(null);
  }, []);

  const updateStaffConfig = useCallback((patch) => {
    setWmStaffMember(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      writeConfig(next);
      return next;
    });
  }, []);

  // ── Location view (replaces org impersonation for localStorage apps) ────────
  const enterOrgView = useCallback((locationId, locationName) => {
    if (!wmCan('org.enter')) return;
    setViewingOrgId(locationId);
    setViewingOrgName(locationName);
  }, [wmCan]);

  const exitOrgView = useCallback(() => {
    setViewingOrgId(null);
    setViewingOrgName(null);
  }, []);

  return (
    <WmStaffContext.Provider value={{
      // Identity
      isWmStaff,
      wmRole,
      wmStaffMember,
      loading: false,      // synchronous — always ready
      // Location view
      viewingOrgId,
      viewingOrgName,
      enterOrgView,
      exitOrgView,
      // Permission helper
      wmCan,
      // Config management
      activateStaff,
      deactivateStaff,
      updateStaffConfig,
    }}>
      {children}
    </WmStaffContext.Provider>
  );
}

export function useWmStaff() {
  const ctx = useContext(WmStaffContext);
  if (!ctx) throw new Error('useWmStaff must be used within WmStaffProvider');
  return ctx;
}
